import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, todayLocal } from "@/lib/api";
import ListSearch, { filterBy } from "@/components/ListSearch";
import PartyPicker from "@/components/PartyPicker";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Send, DollarSign, FileText, Pencil, Copy, Repeat, Mail, RotateCw } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import UpgradeNudge from "@/components/UpgradeNudge";
import CurrencyRateInput from "@/components/CurrencyRateInput";
import BulkActionBar from "@/components/BulkActionBar";
import ItemPickerCombo from "@/components/ItemPickerCombo";
import SendDocDialog from "@/components/SendDocDialog";
import ReplyToField from "@/components/ReplyToField";
import { toast } from "sonner";

const STATUS_STYLES = {
    paid: "bg-emerald-50 text-emerald-700",
    sent: "bg-secondary/10 text-secondary-dark",
    draft: "bg-sand-200 text-muted-foreground",
    overdue: "bg-coral/10 text-coral-dark",
    partial: "bg-amber-50 text-amber-700",
    void: "bg-sand-200 text-muted-foreground",
    open: "bg-secondary/10 text-secondary-dark",
    converted: "bg-sand-200 text-muted-foreground",
};

// Colored pills for the origin of an invoice. Manual invoices (created in the
// UI) intentionally have no badge to avoid visual noise — the absence of a
// pill implies "manual". Webhook-created invoices carry a `webhook_source`
// discriminator on the doc (see routers_public_api.py).
const SOURCE_STYLES = {
    woocommerce: { label: "WOOCOMMERCE", cls: "bg-[#7F54B3]/10 text-[#7F54B3] ring-1 ring-inset ring-[#7F54B3]/25" },
    shopify:     { label: "SHOPIFY",     cls: "bg-[#95BF47]/15 text-[#5E8E3E] ring-1 ring-inset ring-[#5E8E3E]/25" },
    generic:     { label: "GENERIC",     cls: "bg-secondary/10 text-secondary-dark ring-1 ring-inset ring-secondary/25" },
    manual:      { label: "MANUAL",      cls: "bg-sand-200 text-muted-foreground ring-1 ring-inset ring-sand-300" },
};

function SourceBadge({ source, testId }) {
    const key = (source || "").toLowerCase();
    const style = SOURCE_STYLES[key];
    if (!style) return null; // no badge for manual/undefined
    return (
        <span
            data-testid={testId}
            className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${style.cls}`}
            title={`Origin: ${style.label}`}
        >
            {style.label}
        </span>
    );
}

/**
 * Build a one-line description from an invoice/estimate's line items, so the
 * list view surfaces "what's this for?" without forcing the user to open the
 * doc. Joins line descriptions with " · " and truncates with an ellipsis.
 */
function linesSummary(lines, limit = 60) {
    if (!Array.isArray(lines) || lines.length === 0) return "";
    const joined = lines
        .map((ln) => (ln?.description || "").trim())
        .filter(Boolean)
        .join(" · ");
    if (!joined) return "";
    if (joined.length <= limit) return joined;
    return joined.slice(0, limit - 1).trimEnd() + "…";
}

/** Build the search-filter spec for invoice/estimate list rows. */
const INVOICE_SEARCH_FIELDS = [
    "number", "customer_name", "status", "currency",
    (r) => (r.lines || []).map((ln) => ln?.description || "").join(" "),
    "notes",
];

/**
 * Compact hint under the invoice Notes field that shows when the selected
 * customer has a `default_invoice_notes` preset — with a one-click button to
 * apply it or reset back to the default after edits.
 * Kept as a module-level component so React doesn't remount the subtree on
 * every parent render (which would drop focus from the Notes textarea).
 */
function CustomerDefaultNotesHint({ customer, currentNotes, onApply }) {
    const def = (customer?.default_invoice_notes || "").trim();
    if (!def) return null;
    const usingDefault = (currentNotes || "").trim() === def;
    return (
        <div className="mt-1.5 text-[11px] text-muted-foreground flex items-center justify-between" data-testid="notes-default-hint">
            <span>{usingDefault
                ? <>Using <strong>{customer.name}</strong>&apos;s default notes.</>
                : <>Customer has a default notes preset.</>
            }</span>
            <button
                type="button"
                onClick={() => onApply(def)}
                className="text-secondary-dark hover:underline"
                data-testid="notes-restore-default-btn"
            >
                {usingDefault ? "Reset from customer" : "Use customer default"}
            </button>
        </div>
    );
}

export function InvoiceList({ kind = "invoice" }) {
    const { currentOrg } = useAuth();
    const [items, setItems] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [payOpen, setPayOpen] = useState(null);
    const [sendTarget, setSendTarget] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const today = todayLocal();
    const blank = { customer_id: "", issue_date: today, due_date: today, currency: currentOrg?.currency || "KYD", fx_rate: 1,
        notes: "", type: kind, lines: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 0 }],
        is_recurring: false, recurrence: { frequency: "monthly", interval: 1, next_run: today, end_date: "", auto_send: false } };
    const [form, setForm] = useState(blank);
    const [q, setQ] = useState("");
    const [sourceFilter, setSourceFilter] = useState("all");

    const load = async () => {
        const [{ data: inv }, { data: cust }, { data: cat }, { data: ba }] = await Promise.all([
            api.get(`/invoices?type=${kind}`),
            api.get("/customers"),
            api.get("/items").catch(() => ({ data: { items: [] } })),
            api.get("/bank-accounts").catch(() => ({ data: { items: [] } })),
        ]);
        setItems(inv.items || []);
        setCustomers(cust.items || []);
        setCatalog((cat.items || []).filter((i) => i.item_type !== "purchase" && i.active !== false));
        setBankAccounts(ba.items || []);
    };

    useEffect(() => { if (currentOrg) load(); }, [currentOrg, kind]);

    // Source filter — narrows the list by webhook_source before search runs.
    // "manual" matches invoices without a webhook_source (created in the UI).
    const visibleItems = useMemo(() => {
        if (sourceFilter === "all") return items;
        if (sourceFilter === "manual") return items.filter((r) => !r.webhook_source);
        return items.filter((r) => (r.webhook_source || "").toLowerCase() === sourceFilter);
    }, [items, sourceFilter]);

    const totals = useMemo(() => {
        let sub = 0, tax = 0;
        for (const ln of form.lines) {
            const amt = Number(ln.quantity || 0) * Number(ln.unit_price || 0);
            sub += amt;
            if (!currentOrg?.cayman_no_tax_mode) tax += amt * (Number(ln.tax_rate || 0) / 100);
        }
        return { sub, tax, total: sub + tax };
    }, [form.lines, currentOrg]);

    const setLine = (idx, k, v) => {
        const lines = [...form.lines];
        lines[idx] = { ...lines[idx], [k]: v };
        setForm({ ...form, lines });
    };

    const addLine = () => setForm({ ...form, lines: [...form.lines, { description: "", quantity: 1, unit_price: 0, tax_rate: 0 }] });
    const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, x) => x !== i) });

    const submit = async ({ thenResend = false } = {}) => {
        try {
            const recurrence = form.is_recurring ? {
                frequency: form.recurrence.frequency,
                interval: Number(form.recurrence.interval) || 1,
                next_run: form.recurrence.next_run || today,
                end_date: form.recurrence.end_date || null,
                active: true,
                auto_send: !!form.recurrence.auto_send,
            } : null;
            const payload = {
                customer_id: form.customer_id,
                issue_date: form.issue_date,
                due_date: form.due_date,
                currency: form.currency,
                fx_rate: Number(form.fx_rate) || 1,
                notes: form.notes,
                lines: form.lines,
                recurrence,
            };
            let savedId = editing?.id;
            if (editing) {
                await api.patch(`/invoices/${editing.id}`, payload);
                toast.success(`${kind === "invoice" ? "Invoice" : "Estimate"} updated`);
            } else {
                const { data } = await api.post("/invoices", { ...payload, type: kind });
                savedId = data?.id;
                toast.success(`${kind === "invoice" ? "Invoice" : "Estimate"} created`);
            }
            setOpen(false);
            setEditing(null);
            setForm({ ...blank, type: kind });
            await load();
            // "Save & resend" flow: reload the list (so the row has the fresh
            // customer + line data), then pop the confirmation dialog.
            if (thenResend && savedId) {
                // Use a microtask so `items` has re-rendered with the new row.
                setTimeout(() => send(savedId), 60);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ ...blank, type: kind });
        setOpen(true);
    };

    const openEdit = (r) => {
        setEditing(r);
        setForm({
            customer_id: r.customer_id,
            issue_date: r.issue_date,
            due_date: r.due_date,
            currency: r.currency,
            fx_rate: r.fx_rate || 1,
            notes: r.notes || "",
            type: r.type,
            lines: (r.lines || []).map((l) => ({
                description: l.description || "",
                quantity: l.quantity,
                unit_price: l.unit_price,
                tax_rate: l.tax_rate || 0,
                category: l.category || "",
                account_id: l.account_id || null,
            })),
            is_recurring: !!r.recurrence?.active,
            recurrence: {
                frequency: r.recurrence?.frequency || "monthly",
                interval: r.recurrence?.interval || 1,
                next_run: r.recurrence?.next_run || today,
                end_date: r.recurrence?.end_date || "",
                auto_send: !!r.recurrence?.auto_send,
            },
        });
        setOpen(true);
    };

    const duplicate = async (id) => {
        try {
            const { data } = await api.post(`/invoices/${id}/duplicate`);
            toast.success(`Duplicated as ${data.number}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const send = (id) => {
        const inv = items.find((x) => x.id === id);
        if (!inv) return;
        const cust = customers.find((c) => c.id === inv.customer_id);
        // Treat anything past `draft` (and past `open` for estimates) as a
        // resend so the confirmation dialog and audit log make it clear the
        // customer will get a second email.
        const isResend = kind === "invoice"
            ? !(inv.status === "draft" || !inv.status)
            : !(inv.status === "open" || inv.status === "draft" || !inv.status);
        setSendTarget({
            id,
            kind: kind === "estimate" ? "estimate" : "invoice",
            number: inv.number,
            resend: isResend,
            // Pass customer_id so the dialog can re-fetch the latest record
            // (handles the inline-created-customer race).
            customer_id: inv.customer_id,
            customer_name: inv.customer_name,
            customer_email: cust?.email || inv.customer_email || null,
        });
    };

    const convert = async (id) => {
        try { await api.post(`/invoices/${id}/convert`); toast.success("Converted to invoice"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        const r = items.find((x) => x.id === id);
        const paidAmt = Number(r?.amount_paid || 0);
        const payWarning = paidAmt > 0
            ? `\n\nThis ${kind} has ${fmtMoney(paidAmt, r.currency)} in recorded payment(s). Deleting it will also PERMANENTLY DELETE those payment records and reverse them from your bank ledger.`
            : "";
        if (!window.confirm(`Delete ${kind} ${r?.number || ""}?${payWarning}\n\nThis action cannot be undone.`)) return;
        try {
            const { data } = await api.delete(`/invoices/${id}`);
            if (data?.payments_deleted > 0) toast.success(`${kind === "invoice" ? "Invoice" : "Estimate"} deleted (${data.payments_deleted} payment${data.payments_deleted === 1 ? "" : "s"} also removed)`);
            else toast.success(`${kind === "invoice" ? "Invoice" : "Estimate"} deleted`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const recordPay = async (id, { amount, method = "manual", reference = null, paidOn = null, accountId = null, sendReceipt = false, replyTo = null }) => {
        try {
            const { data } = await api.post(`/invoices/${id}/payments`, {
                amount: Number(amount),
                method,
                reference: reference || null,
                paid_on: paidOn || null,
                account_id: accountId || null,
                send_receipt: !!sendReceipt,
                reply_to: (replyTo || "").trim() || null,
            });
            toast.success("Payment recorded");
            if (sendReceipt) {
                if (data?.email?.sent) {
                    toast.success(`Receipt emailed to ${data.email.to}`);
                } else if (data?.email) {
                    toast.error(`Receipt not sent — ${data.email.detail || "unknown error"}`);
                }
            }
            setPayOpen(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const emailReceipt = (id) => {
        const inv = items.find((x) => x.id === id);
        if (!inv) return;
        const cust = customers.find((c) => c.id === inv.customer_id);
        setSendTarget({
            id,
            kind: "receipt",
            number: inv.number,
            customer_id: inv.customer_id,
            customer_name: inv.customer_name,
            customer_email: cust?.email || inv.customer_email || null,
        });
    };

    const toggleSel = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const bulkDelete = async () => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        const paidCount = ids.filter((id) => Number(items.find((r) => r.id === id)?.amount_paid || 0) > 0).length;
        const warn = paidCount > 0 ? `\n\n${paidCount} of these have recorded payments that will ALSO be deleted.` : "";
        if (!window.confirm(`Delete ${ids.length} ${kind}${ids.length === 1 ? "" : "s"}?${warn}\n\nThis action cannot be undone.`)) return;
        setBulkBusy(true);
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/invoices/${id}`)));
        const failed = results.filter((r) => r.status === "rejected").length;
        const ok = results.length - failed;
        const paymentsDeleted = results
            .filter((r) => r.status === "fulfilled")
            .reduce((acc, r) => acc + (r.value?.data?.payments_deleted || 0), 0);
        if (ok) toast.success(`${ok} ${kind}${ok === 1 ? "" : "s"} deleted${paymentsDeleted ? ` (${paymentsDeleted} linked payment${paymentsDeleted === 1 ? "" : "s"} also removed)` : ""}`);
        if (failed) toast.error(`${failed} could not be deleted`);
        setSelected(new Set());
        setBulkBusy(false);
        load();
    };

    return (
        <div>
            <PageHeader
                subtitle={kind === "invoice" ? "Sales" : "Sales"}
                title={kind === "invoice" ? "Invoices" : "Estimates"}
                action={
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ ...blank, type: kind }); } }}>
                        <DialogTrigger asChild>
                            <Button onClick={openCreate} className="bg-ocean-700 text-white rounded-xl" data-testid={`new-${kind}-btn`}>
                                <Plus className="h-4 w-4 mr-1.5" /> New {kind}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? `Edit ${kind}` : `New ${kind}`}{editing?.number ? ` · ${editing.number}` : ""}</DialogTitle></DialogHeader>
                            <div className="grid sm:grid-cols-2 gap-4 mt-2">
                                <div>
                                    <Label>Customer</Label>
                                    <PartyPicker
                                        kind="customer"
                                        options={customers}
                                        value={form.customer_id}
                                        onChange={(v) => {
                                            // When the user picks a customer, auto-populate notes from that
                                            // customer's default_invoice_notes — but only when creating a new
                                            // doc AND the notes field is still empty, so we never trample the
                                            // user's own edits or existing doc content.
                                            setForm((prev) => {
                                                const patch = { ...prev, customer_id: v };
                                                if (!editing && !(prev.notes || "").trim() && v) {
                                                    const pick = customers.find((c) => c.id === v);
                                                    if (pick?.default_invoice_notes) patch.notes = pick.default_invoice_notes;
                                                }
                                                return patch;
                                            });
                                        }}
                                        onCreated={(nc) => setCustomers([...customers, nc])}
                                        placeholder="Select customer"
                                        testId="invoice-customer-select"
                                    />
                                </div>
                                <div>
                                    <Label>Issue date</Label>
                                    <Input className="mt-1.5" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Due date</Label>
                                    <Input className="mt-1.5" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                                </div>
                            </div>
                            <div className="mt-4">
                                <CurrencyRateInput
                                    baseCurrency={currentOrg?.currency || "KYD"}
                                    currency={form.currency}
                                    fxRate={form.fx_rate}
                                    amount={totals.total}
                                    onCurrency={(c) => setForm({ ...form, currency: c })}
                                    onFxRate={(r) => setForm({ ...form, fx_rate: r })}
                                    testId="invoice-fx"
                                />
                            </div>
                            <div className="mt-6">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Line items</div>
                                    {catalog.length > 0 && (
                                        <span className="text-[11px] text-muted-foreground">Tip: pick from the item catalog to auto-fill</span>
                                    )}
                                </div>
                                <div className="grid grid-cols-12 gap-2 px-1 pb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                                    <div className="col-span-5">Description</div>
                                    <div className="col-span-2">Qty</div>
                                    <div className="col-span-2">Unit price</div>
                                    <div className="col-span-2">Tax %</div>
                                    <div className="col-span-1" />
                                </div>
                                <div className="space-y-2">
                                    {form.lines.map((ln, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                            <div className="col-span-5 flex gap-1.5">
                                                <Input className="flex-1" placeholder="Description" value={ln.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`invoice-line-desc-${i}`} />
                                                <ItemPickerCombo
                                                    items={catalog}
                                                    filterType="sales"
                                                    currency={form.currency}
                                                    testId={`invoice-line-${i}-item`}
                                                    currentRate={ln.unit_price}
                                                    currentTaxRate={currentOrg?.cayman_no_tax_mode ? 0 : ln.tax_rate}
                                                    onCreate={(it) => setCatalog((prev) => [...prev, it])}
                                                    onPick={(it) => {
                                                        const lines = [...form.lines];
                                                        lines[i] = {
                                                            ...lines[i],
                                                            description: it.name + (it.description ? ` — ${it.description}` : ""),
                                                            unit_price: it.rate || 0,
                                                            tax_rate: currentOrg?.cayman_no_tax_mode ? 0 : (it.tax_rate || 0),
                                                            item_id: it.id,
                                                        };
                                                        setForm({ ...form, lines });
                                                    }}
                                                />
                                            </div>
                                            <Input className="col-span-2" type="number" placeholder="Qty" value={ln.quantity} onChange={(e) => setLine(i, "quantity", Number(e.target.value))} />
                                            <Input className="col-span-2" type="number" placeholder="Price" value={ln.unit_price} onChange={(e) => setLine(i, "unit_price", Number(e.target.value))} />
                                            <Input className="col-span-2" type="number" placeholder="Tax %" value={ln.tax_rate} disabled={currentOrg?.cayman_no_tax_mode} onChange={(e) => setLine(i, "tax_rate", Number(e.target.value))} />
                                            <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeLine(i)}><Trash2 className="h-4 w-4" /></Button>
                                        </div>
                                    ))}
                                </div>
                                <Button variant="outline" size="sm" onClick={addLine} className="mt-3 rounded-lg">
                                    <Plus className="h-4 w-4 mr-1" /> Add line
                                </Button>
                            </div>
                            <div className="mt-4 rounded-2xl bg-sand-100 p-4 grid grid-cols-3 gap-4 text-sm">
                                <div><div className="text-muted-foreground text-xs">Subtotal</div><div className="font-mono">{fmtMoney(totals.sub, form.currency)}</div></div>
                                <div><div className="text-muted-foreground text-xs">Tax {currentOrg?.cayman_no_tax_mode && "· Cayman 0%"}</div><div className="font-mono">{fmtMoney(totals.tax, form.currency)}</div></div>
                                <div><div className="text-muted-foreground text-xs">Total</div><div className="font-mono font-medium text-ocean-700">{fmtMoney(totals.total, form.currency)}</div></div>
                            </div>

                            {/* Free-form notes shown to the customer on the printed / emailed doc.
                                Handy for payment terms, purchase-order references, thank-you notes, etc.
                                Auto-fills from the customer's `default_invoice_notes` when a customer is
                                selected on a new doc. */}
                            <div className="mt-6">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor={`${kind}-notes`} className="text-ocean-700">Notes</Label>
                                    <span className="text-[11px] text-muted-foreground">
                                        Visible to the customer on the {kind} PDF &amp; email · {(form.notes || "").length}/1000
                                    </span>
                                </div>
                                <Textarea
                                    id={`${kind}-notes`}
                                    rows={3}
                                    maxLength={1000}
                                    value={form.notes || ""}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    placeholder={
                                        kind === "invoice"
                                            ? "e.g. Thanks for your business! Net-30. Reference PO-1234 when paying."
                                            : "e.g. Quote valid for 30 days. Deposit of 50% required to start."
                                    }
                                    className="mt-1.5"
                                    data-testid={`${kind}-notes-input`}
                                />
                                {!editing && kind === "invoice" && (
                                    <CustomerDefaultNotesHint
                                        customer={customers.find((c) => c.id === form.customer_id)}
                                        currentNotes={form.notes}
                                        onApply={(def) => setForm((prev) => ({ ...prev, notes: def }))}
                                    />
                                )}
                            </div>

                            {kind === "invoice" && (
                                <div className="mt-6 rounded-2xl border border-sand-300 p-4">
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={form.is_recurring}
                                            onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
                                            data-testid="invoice-recurring-toggle"
                                            className="mt-1 h-4 w-4 accent-secondary"
                                        />
                                        <div>
                                            <div className="inline-flex items-center gap-2 font-medium text-ocean-700">
                                                <Repeat className="h-4 w-4 text-secondary" /> Make this invoice recurring
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                BlueLedger will automatically generate a fresh draft invoice on the schedule you set.
                                            </div>
                                        </div>
                                    </label>
                                    {form.is_recurring && (
                                        <div className="mt-4 grid grid-cols-2 gap-3">
                                            <div>
                                                <Label className="text-xs">Frequency</Label>
                                                <Select value={form.recurrence.frequency} onValueChange={(v) => setForm({ ...form, recurrence: { ...form.recurrence, frequency: v } })}>
                                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="weekly">Weekly</SelectItem>
                                                        <SelectItem value="monthly">Monthly</SelectItem>
                                                        <SelectItem value="quarterly">Quarterly</SelectItem>
                                                        <SelectItem value="yearly">Yearly</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div>
                                                <Label className="text-xs">Every</Label>
                                                <Input type="number" min="1" className="mt-1.5" value={form.recurrence.interval} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, interval: e.target.value } })} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Next run</Label>
                                                <Input type="date" className="mt-1.5" value={form.recurrence.next_run} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, next_run: e.target.value } })} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Ends on (optional)</Label>
                                                <Input type="date" className="mt-1.5" value={form.recurrence.end_date} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, end_date: e.target.value } })} />
                                            </div>
                                            <label className="col-span-2 mt-1 flex items-start gap-3 rounded-xl border border-sand-200 bg-sand-50 p-3 cursor-pointer hover:border-secondary/40 transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={!!form.recurrence.auto_send}
                                                    onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, auto_send: e.target.checked } })}
                                                    data-testid="invoice-recurring-autosend"
                                                    className="mt-1 h-4 w-4 accent-secondary"
                                                />
                                                <div className="text-sm">
                                                    <div className="font-medium text-ocean-700">Automatically email each new invoice to the customer</div>
                                                    <div className="text-xs text-muted-foreground mt-0.5">
                                                        Each cycle, BlueLedger will generate the invoice AND send it via Mailgun to the customer's email on file. Truly hands-off.
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            )}

                            <DialogFooter className="mt-6">
                                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                                {/* Show "Save & resend" for invoices/estimates already delivered to the
                                    customer so a single click ships the updated document. */}
                                {editing && (
                                    kind === "invoice"
                                        ? !["draft", "paid", "void", "converted"].includes(editing.status)
                                        : !["open", "draft", "converted", "void"].includes(editing.status)
                                ) && (
                                    <Button
                                        variant="outline"
                                        onClick={() => submit({ thenResend: true })}
                                        className="border-secondary/50 text-secondary-dark hover:bg-secondary/10"
                                        data-testid={`submit-and-resend-${kind}-btn`}
                                    >
                                        <RotateCw className="h-4 w-4 mr-1.5" /> Save &amp; resend
                                    </Button>
                                )}
                                <Button onClick={() => submit()} className="bg-ocean-700 text-white" data-testid={`submit-${kind}-btn`}>{editing ? "Save changes" : `Create ${kind}`}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />

            {items.length === 0 ? (
                <EmptyState icon={FileText} title={`No ${kind}s yet`} description={`Create your first ${kind} and start collecting payments.`} />
            ) : (
                <>
                    {kind === "invoice" && <UpgradeNudge resource="invoices" />}
                    <BulkActionBar
                        count={selected.size}
                        label={kind}
                        onDelete={bulkDelete}
                        onClear={() => setSelected(new Set())}
                        busy={bulkBusy}
                        warning="Linked payments will also be deleted."
                        testId={`${kind}-bulk`}
                    />
                    <div className="mb-4 flex items-center gap-3 flex-wrap">
                        <ListSearch value={q} onChange={setQ} placeholder={`Search ${kind}s by number, customer, description or status…`} testId={`${kind}s-search`} />
                        {kind === "invoice" && (
                            <Select value={sourceFilter} onValueChange={setSourceFilter}>
                                <SelectTrigger className="w-[180px] rounded-xl border-sand-300 bg-white" data-testid="invoices-source-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All sources</SelectItem>
                                    <SelectItem value="manual">Manual only</SelectItem>
                                    <SelectItem value="woocommerce">WooCommerce</SelectItem>
                                    <SelectItem value="shopify">Shopify</SelectItem>
                                    <SelectItem value="generic">Generic webhook</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                        {(q || sourceFilter !== "all") && (
                            <span className="text-xs text-muted-foreground" data-testid="invoices-filter-count">
                                {filterBy(visibleItems, q, INVOICE_SEARCH_FIELDS).length} of {items.length}
                            </span>
                        )}
                    </div>
                    <div className="rounded-3xl bg-white border border-sand-200 shadow-card overflow-hidden">
                        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 [scrollbar-width:thin] relative">
                            <table className="w-full text-sm min-w-[720px] sm:min-w-0">
                            <thead className="bg-sand-100 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-3 w-8">
                                        <input
                                            type="checkbox"
                                            checked={items.length > 0 && selected.size === filterBy(visibleItems, q, INVOICE_SEARCH_FIELDS).length}
                                            onChange={(e) => {
                                                if (e.target.checked) setSelected(new Set(filterBy(visibleItems, q, ["number", "customer_name", "status", "currency"]).map((r) => r.id)));
                                                else setSelected(new Set());
                                            }}
                                            className="h-4 w-4 accent-secondary cursor-pointer"
                                            data-testid={`${kind}s-select-all`}
                                            aria-label={`Select all ${kind}s`}
                                        />
                                    </th>
                                    <th className="text-left px-5 py-3">{kind === "invoice" ? "Invoice #" : "Estimate #"}</th>
                                    <th className="text-left px-5 py-3">Customer</th>
                                    <th className="text-left px-5 py-3">Description</th>
                                    <th className="text-left px-5 py-3">Issue</th>
                                    <th className="text-left px-5 py-3">Due</th>
                                    <th className="text-right px-5 py-3">Total</th>
                                    <th className="text-right px-5 py-3">Balance</th>
                                    <th className="text-left px-5 py-3">Status</th>
                                    <th className="px-5 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-sand-200">
                                {filterBy(visibleItems, q, INVOICE_SEARCH_FIELDS).map((r) => (
                                    <tr key={r.id} className={`hover:bg-sand-50/50 ${selected.has(r.id) ? "bg-secondary/5" : ""}`}>
                                        <td className="px-3 py-3 text-center">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(r.id)}
                                                onChange={() => toggleSel(r.id)}
                                                className="h-4 w-4 accent-secondary cursor-pointer"
                                                data-testid={`${kind}-select-${r.id}`}
                                                aria-label={`Select ${kind} ${r.number}`}
                                            />
                                        </td>
                                        <td className="px-5 py-3 font-mono text-xs">
                                            <div className="flex items-center gap-2">
                                                <span>{r.number}</span>
                                                <SourceBadge source={r.webhook_source} testId={`${kind}-source-${r.id}`} />
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 font-medium text-ocean-700">{r.customer_name}</td>
                                        <td className="px-5 py-3 text-muted-foreground max-w-[260px] truncate" title={(r.lines || []).map((ln) => ln?.description || "").filter(Boolean).join(" · ") || r.notes || ""} data-testid={`${kind}-description-${r.id}`}>
                                            {linesSummary(r.lines) || <span className="text-muted-foreground/60 italic">—</span>}
                                        </td>
                                        <td className="px-5 py-3 text-muted-foreground">{fmtDate(r.issue_date)}</td>
                                        <td className="px-5 py-3 text-muted-foreground">{fmtDate(r.due_date)}</td>
                                        <td className="px-5 py-3 font-mono text-right">
                                            <div className="inline-flex items-center gap-2 justify-end">
                                                {r.currency && currentOrg?.currency && r.currency !== currentOrg.currency && (
                                                    <span className="text-[10px] font-medium tracking-wide text-secondary-dark bg-secondary/10 rounded-md px-1.5 py-0.5" data-testid={`invoice-fx-chip-${r.id}`} title={`Stored rate: 1 ${r.currency} = ${(r.fx_rate || 1).toFixed(4)} ${currentOrg.currency}`}>
                                                        {r.currency} · {(r.fx_rate || 1).toFixed(2)}
                                                    </span>
                                                )}
                                                <span>{fmtMoney(r.total, r.currency)}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 font-mono text-right">{fmtMoney(r.balance, r.currency)}</td>
                                        <td className="px-5 py-3"><Badge className={`border-0 ${STATUS_STYLES[r.status] || ""}`}>{r.status}</Badge></td>
                                        <td className="px-5 py-3 text-right whitespace-nowrap">
                                            {r.recurrence?.active && (
                                                <span title={`Recurring ${r.recurrence.frequency} · next ${r.recurrence.next_run}`} className="inline-flex items-center mr-1.5 text-secondary" data-testid={`recurring-badge-${r.id}`}>
                                                    <Repeat className="h-4 w-4" />
                                                </span>
                                            )}
                                            {r.status !== "void" && r.status !== "converted" && (
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-${kind}-${r.id}`} title="Edit"><Pencil className="h-4 w-4 text-muted-foreground" /></Button>
                                            )}
                                            <Button variant="ghost" size="sm" onClick={() => duplicate(r.id)} data-testid={`duplicate-${kind}-${r.id}`} title="Duplicate"><Copy className="h-4 w-4 text-muted-foreground" /></Button>
                                            {kind === "invoice" && !["paid", "void", "converted"].includes(r.status) && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => send(r.id)}
                                                    data-testid={r.status === "draft" ? `send-${r.id}` : `resend-${r.id}`}
                                                    title={r.status === "draft" ? "Send invoice" : "Resend updated invoice"}
                                                >
                                                    {r.status === "draft"
                                                        ? <Send className="h-4 w-4" />
                                                        : <RotateCw className="h-4 w-4 text-secondary-dark" />}
                                                </Button>
                                            )}
                                            {kind === "estimate" && !["converted", "void"].includes(r.status) && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => send(r.id)}
                                                    data-testid={r.status === "open" || r.status === "draft" ? `send-${r.id}` : `resend-${r.id}`}
                                                    title={r.status === "open" || r.status === "draft" ? "Send estimate" : "Resend updated estimate"}
                                                >
                                                    {r.status === "open" || r.status === "draft"
                                                        ? <Send className="h-4 w-4" />
                                                        : <RotateCw className="h-4 w-4 text-secondary-dark" />}
                                                </Button>
                                            )}
                                            {kind === "invoice" && (r.status === "paid" || r.status === "partial") && (
                                                <Button variant="ghost" size="sm" onClick={() => emailReceipt(r.id)} data-testid={`email-receipt-${r.id}`} title="Email receipt to customer"><Mail className="h-4 w-4 text-muted-foreground" /></Button>
                                            )}
                                            {kind === "invoice" && r.status !== "paid" && r.status !== "void" && (
                                                <Button size="sm" variant="outline" onClick={() => setPayOpen(r)} data-testid={`pay-${r.id}`} className="h-8 rounded-lg border-secondary/40 text-secondary-dark hover:bg-secondary/10">
                                                    <DollarSign className="h-3.5 w-3.5 mr-1" /> Record payment
                                                </Button>
                                            )}
                                            {kind === "estimate" && r.status !== "converted" && (
                                                <Button variant="ghost" size="sm" onClick={() => convert(r.id)} data-testid={`convert-${r.id}`}>Convert</Button>
                                            )}
                                            <Button variant="ghost" size="sm" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                </>
            )}

            <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
                    {payOpen && (
                        <PaymentForm
                            invoice={payOpen}
                            customers={customers}
                            bankAccounts={bankAccounts}
                            today={today}
                            onSubmit={(payload) => recordPay(payOpen.id, payload)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <SendDocDialog
                doc={sendTarget}
                onClose={() => setSendTarget(null)}
                onSent={() => { setSendTarget(null); load(); }}
            />
        </div>
    );
}

function PaymentForm({ invoice, customers, bankAccounts, today, onSubmit }) {
    const [amt, setAmt] = useState(invoice.balance.toString());
    const [method, setMethod] = useState("manual");
    const [reference, setReference] = useState("");
    const [paidOn, setPaidOn] = useState(today);
    const [accountId, setAccountId] = useState(bankAccounts?.[0]?.id || "");
    const [sendReceipt, setSendReceipt] = useState(false);
    const [replyTo, setReplyTo] = useState(undefined);
    const customer = (customers || []).find((c) => c.id === invoice.customer_id);
    const hasEmail = !!customer?.email;

    return (
        <div className="space-y-4">
            <div className="rounded-2xl bg-sand-100 p-4 text-sm">
                <div className="text-muted-foreground text-xs uppercase tracking-wider">{invoice.number}</div>
                <div className="font-medium text-ocean-700 mt-1">{invoice.customer_name}</div>
                <div className="mt-2 text-muted-foreground text-xs">Balance: <span className="font-mono">{fmtMoney(invoice.balance, invoice.currency)}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label>Amount</Label>
                    <Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} className="mt-1.5" data-testid="payment-amount" />
                </div>
                <div>
                    <Label>Date received</Label>
                    <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className="mt-1.5" data-testid="payment-paid-on" />
                </div>
                <div>
                    <Label>Method</Label>
                    <Select value={method} onValueChange={setMethod}>
                        <SelectTrigger className="mt-1.5" data-testid="payment-method"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="manual">Cash / manual</SelectItem>
                            <SelectItem value="bank">Bank transfer</SelectItem>
                            <SelectItem value="card">Card</SelectItem>
                            <SelectItem value="cheque">Cheque</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Deposit to account</Label>
                    <Select value={accountId} onValueChange={setAccountId}>
                        <SelectTrigger className="mt-1.5" data-testid="payment-account">
                            <SelectValue placeholder={bankAccounts?.length ? "Select account" : "No bank accounts"} />
                        </SelectTrigger>
                        <SelectContent>
                            {(bankAccounts || []).map((a) => (
                                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div>
                <Label>Reference (optional)</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. wire transfer #ABC123" className="mt-1.5" />
            </div>
            <label className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${hasEmail ? "border-sand-300 hover:border-secondary/40" : "border-sand-200 opacity-60 cursor-not-allowed"}`}>
                <input
                    type="checkbox"
                    checked={sendReceipt}
                    disabled={!hasEmail}
                    onChange={(e) => setSendReceipt(e.target.checked)}
                    data-testid="email-receipt-checkbox"
                    className="mt-1 h-4 w-4 accent-secondary"
                />
                <div className="text-sm">
                    <div className="font-medium text-ocean-700">Email paid invoice to customer</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {hasEmail
                            ? `Sends a receipt to ${customer.email}`
                            : "Customer has no email on file — add one on the customer record to enable this."}
                    </div>
                </div>
            </label>
            {sendReceipt && hasEmail && (
                <ReplyToField value={replyTo} onChange={setReplyTo} testId="payment-reply-to" />
            )}
            <Button
                onClick={() => onSubmit({ amount: amt, method, reference, paidOn, accountId, sendReceipt, replyTo })}
                className="w-full bg-ocean-700 text-white"
                data-testid="submit-payment-btn"
            >
                Record payment
            </Button>
        </div>
    );
}

export default function Invoices() {
    return <InvoiceList kind="invoice" />;
}
